import { theme as baseTheme } from './tokens';
import type { Theme, ThemeOverride, ThemeExport } from './types';

const THEME_VERSION = '1.0.0';

/**
 * Deep merge two objects, with source taking precedence
 */
export const deepMerge = <T extends Record<string, unknown>>(
  target: T,
  source: Partial<T>
): T => {
  const result = { ...target };

  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const sourceValue = source[key];
      const targetValue = target[key];

      if (
        sourceValue &&
        typeof sourceValue === 'object' &&
        !Array.isArray(sourceValue) &&
        targetValue &&
        typeof targetValue === 'object' &&
        !Array.isArray(targetValue)
      ) {
        result[key] = deepMerge(
          targetValue as Record<string, unknown>,
          sourceValue as Record<string, unknown>
        ) as T[Extract<keyof T, string>];
      } else if (sourceValue !== undefined) {
        result[key] = sourceValue as T[Extract<keyof T, string>];
      }
    }
  }

  return result;
};

/**
 * Merge base theme with overrides
 */
export const mergeTheme = (override: ThemeOverride): Theme => {
  return deepMerge(baseTheme, override as Partial<typeof baseTheme>) as Theme;
};

/**
 * Convert camelCase to kebab-case
 */
const toKebabCase = (str: string): string => {
  return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
};

/**
 * Generate CSS variables from theme
 */
export const generateCssVariables = (theme: Theme): Record<string, string> => {
  const variables: Record<string, string> = {};

  // Colors
  for (const [key, value] of Object.entries(theme.colors)) {
    variables[`--color-${toKebabCase(key)}`] = value;
  }

  // Typography
  for (const [key, value] of Object.entries(theme.typography)) {
    const cssKey = toKebabCase(key);
    variables[`--${cssKey}`] = String(value);
  }

  // Spacing
  for (const [key, value] of Object.entries(theme.spacing)) {
    variables[`--spacing-${key}`] = value;
  }

  // Border radius
  for (const [key, value] of Object.entries(theme.borderRadius)) {
    variables[`--radius-${key}`] = value;
  }

  // Shadows
  for (const [key, value] of Object.entries(theme.shadows)) {
    variables[`--shadow-${key}`] = value;
  }

  // Sidebar
  for (const [key, value] of Object.entries(theme.sidebar)) {
    variables[`--sidebar-${toKebabCase(key)}`] = value;
  }

  return variables;
};

/**
 * Apply CSS variables to document root
 */
export const applyCssVariables = (variables: Record<string, string>): void => {
  const root = document.documentElement;
  for (const [key, value] of Object.entries(variables)) {
    root.style.setProperty(key, value);
  }
};

/**
 * Remove applied CSS variables
 */
export const removeCssVariables = (variables: Record<string, string>): void => {
  const root = document.documentElement;
  for (const key of Object.keys(variables)) {
    root.style.removeProperty(key);
  }
};

/**
 * Export theme as JSON string
 */
export const exportThemeToJson = (
  override: ThemeOverride,
  name = 'Custom Theme'
): string => {
  const exportData: ThemeExport = {
    version: THEME_VERSION,
    name,
    createdAt: new Date().toISOString(),
    override,
  };
  return JSON.stringify(exportData, null, 2);
};

/**
 * Parse and validate theme JSON
 */
export const parseThemeJson = (json: string): ThemeOverride | null => {
  try {
    const data = JSON.parse(json) as ThemeExport;

    // Validate version
    if (!data.version || !data.override) {
      console.warn('[Theme] Invalid theme JSON: missing version or override');
      return null;
    }

    // Validate override structure
    const { override } = data;
    if (typeof override !== 'object') {
      console.warn('[Theme] Invalid theme JSON: override is not an object');
      return null;
    }

    return override;
  } catch (error) {
    console.error('[Theme] Failed to parse theme JSON:', error);
    return null;
  }
};

/**
 * Parse tenant theme override from settings
 */
export const parseTenantThemeOverride = (
  themeOverrideJson: string | undefined
): ThemeOverride => {
  if (!themeOverrideJson) return {};

  try {
    const parsed = JSON.parse(themeOverrideJson);
    // Could be either ThemeExport format or direct ThemeOverride
    if (parsed.override) {
      return parsed.override as ThemeOverride;
    }
    return parsed as ThemeOverride;
  } catch {
    console.warn('[Theme] Failed to parse tenant theme override');
    return {};
  }
};

/**
 * Validate color value
 */
export const isValidColor = (color: string): boolean => {
  // Check hex color
  if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color)) {
    return true;
  }
  // Check rgb/rgba
  if (/^rgba?\([^)]+\)$/.test(color)) {
    return true;
  }
  // Check hsl/hsla
  if (/^hsla?\([^)]+\)$/.test(color)) {
    return true;
  }
  return false;
};

/**
 * Get contrast color (black or white) for a given background
 */
export const getContrastColor = (hexColor: string): string => {
  // Remove # if present
  const hex = hexColor.replace('#', '');

  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.5 ? '#000000' : '#ffffff';
};
